// OPTIMIZED CategoryPage.tsx - Better SEO, Fewer Ads, More Revenue
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toolCategories } from '../data/toolsByCategory';
import ToolCard from '../components/Home/ToolCard';
import slugify from '../utils/slugify';
import useIsMobile from '../hooks/useIsMobile';
import { Category } from '../data/types';
import InFeedAd from '../components/Ads/InFeedAd';

const CategoryPage = () => {
  const { '*': wildcard } = useParams();
  const slug = wildcard?.split('/')[0] || '';
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [toolSearch, setToolSearch] = useState('');

  const toolSearchText = toolSearch.trim().toLowerCase();

  const currentCategory = useMemo(() => 
    slug ? toolCategories.find(cat => slugify(cat.title) === slug) : null,
    [slug]
  );

  // EARLY RETURN - Show CategoriesGrid if no category found
  if (!currentCategory) {
    return <CategoriesGrid />;
  }

  const filteredTools = useMemo(() => {
    if (!currentCategory) return [];
    
    return currentCategory.tools.filter(tool => {
      const searchable = [
        tool.title,
        tool.description,
        tool.longDescription,
        ...(tool.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return searchable.includes(toolSearchText);
    });
  }, [currentCategory, toolSearchText]);

  const freeToolsCount = useMemo(() => 
    currentCategory ? currentCategory.tools.filter(t => t.isFree).length : 0,
    [currentCategory]
  );

  // IMPROVED SEO DATA - Now guaranteed currentCategory exists
  const seoData = useMemo(() => {
    const categoryName = currentCategory.title;
    const toolCount = currentCategory.tools.length;
    const searchContext = toolSearchText ? ` for ${toolSearchText}` : '';

    return {
      title: `${categoryName} AI Tools - ${toolCount}+ Solutions${searchContext}`,
      description: `Find ${toolCount}+ ${categoryName.toLowerCase()} AI tools. Compare features, pricing, and reviews. ${freeToolsCount}+ free options available. Updated January 2025.`,
      keywords: `${categoryName} AI tools, ${categoryName} software, best ${categoryName} apps, free ${categoryName} tools`,
      canonical: `https://tooliq.ai/categories/${slug}`
    };
  }, [currentCategory, slug, toolSearchText, freeToolsCount]);

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": seoData.title,
            "description": seoData.description,
            "url": seoData.canonical,
            "about": {
              "@type": "Thing",
              "name": currentCategory.title
            },
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": currentCategory.tools.length,
              "itemListElement": currentCategory.tools.slice(0, 10).map((tool, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "SoftwareApplication",
                  "name": tool.title,
                  "description": tool.description,
                  "applicationCategory": currentCategory.title
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/60 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-6 gap-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/categories')}
                  className="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-2 transition-colors group"
                >
                  <span className="group-hover:-translate-x-0.5 transition-transform">←</span>
                  All Categories
                </button>
                <span className="text-gray-300">/</span>
                <h1 className="text-2xl font-bold text-gray-900">{currentCategory.title}</h1>
              </div>

              <div className="relative w-full sm:w-80">
                <input
                  type="text"
                  value={toolSearch}
                  onChange={e => setToolSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 pr-10 text-base focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-50/50"
                  placeholder={`Search ${currentCategory.title.toLowerCase()} tools...`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {toolSearch ? (
                    <button
                      onClick={() => setToolSearch('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ADDED: SEO-RICH CONTENT SECTION */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
            <div className="prose max-w-none">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                {currentCategory.title} AI Tools - Complete Guide
              </h2>
              <p className="text-gray-700 mb-3 text-lg">
                Discover <strong>{currentCategory.tools.length} {currentCategory.title.toLowerCase()} AI tools</strong> with 
                detailed comparisons, pricing information, and expert reviews. 
                {freeToolsCount > 0 && ` ${freeToolsCount}+ tools offer free plans or trials.`}
              </p>
              
              <div className="flex flex-wrap gap-3 mt-4">
                <span className="inline-flex items-center gap-1 bg-green-50 px-3 py-1 rounded-full text-sm border border-green-200">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {freeToolsCount}+ Free Options
                </span>
                <span className="inline-flex items-center gap-1 bg-blue-50 px-3 py-1 rounded-full text-sm border border-blue-200">
                  <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Expert Reviewed
                </span>
                <span className="inline-flex items-center gap-1 bg-purple-50 px-3 py-1 rounded-full text-sm border border-purple-200">
                  <svg className="w-3 h-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Updated Daily
                </span>
              </div>

              {/* Internal Links */}
              <p className="text-gray-700 mt-4 text-sm">
                <strong>Related:</strong>{' '}
                <Link to="/tools" className="text-purple-600 hover:underline">Browse all AI tools</Link>
                {' • '}
                <Link to="/categories" className="text-purple-600 hover:underline">View all categories</Link>
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {filteredTools.length} {filteredTools.length === 1 ? 'Tool' : 'Tools'} Found
              </h2>
              {toolSearch && (
                <p className="text-gray-600 mt-1">
                  in {currentCategory.title} for "<span className="text-purple-600 font-medium">{toolSearch}</span>"
                </p>
              )}
            </div>
            
            <div className="hidden sm:flex items-center gap-4 text-sm text-gray-600">
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full">
                {freeToolsCount} free
              </span>
              <span>{currentCategory.tools.length} total</span>
            </div>
          </div>

          {/* Tools Grid - SINGLE AD after 12th tool */}
          {filteredTools.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTools.map((tool, index) => (
                <React.Fragment key={`${tool.title}-${index}`}>
                  <ToolCard
                    {...tool}
                    icon={tool.icon ?? '/default-icon.png'}
                    category={currentCategory.title}
                  />

                  {/* SINGLE STRATEGIC AD - Only after 12th tool */}
                  {index === 11 && filteredTools.length > 12 && (
                    <div className="col-span-1 sm:col-span-2 lg:col-span-3 xl:col-span-4 my-4">
                      <InFeedAd />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-200">
              <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No tools found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8 text-lg">
                {toolSearch 
                  ? `No ${currentCategory.title.toLowerCase()} tools found for "${toolSearch}". Try different search terms.`
                  : `No tools available in ${currentCategory.title} yet. Check back soon!`
                }
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                {toolSearch && (
                  <button
                    onClick={() => setToolSearch('')}
                    className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 font-semibold"
                  >
                    Clear Search
                  </button>
                )}
                <button
                  onClick={() => navigate('/categories')}
                  className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-purple-300 hover:bg-purple-50 font-semibold"
                >
                  Browse Categories
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// OPTIMIZED CategoriesGrid Component
const CategoriesGrid = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCategories = useMemo(() => 
    toolCategories.filter(cat =>
      cat.title.toLowerCase().includes(searchTerm.trim().toLowerCase())
    ),
    [searchTerm]
  );

  const totalTools = useMemo(() => 
    toolCategories.reduce((sum, cat) => sum + cat.tools.length, 0),
    []
  );

  const totalFreeTools = useMemo(() =>
    toolCategories.flatMap(cat => cat.tools).filter(tool => tool.isFree).length,
    []
  );

  return (
    <>
      <Helmet>
        <title>{`AI Tools by Category - Browse ${toolCategories.length}+ Specialized Solutions`}</title>
        <meta 
          name="description" 
          content={`Explore ${toolCategories.length}+ AI tool categories including writing, image generation, coding, marketing, and more. Find specialized solutions among ${totalTools}+ tools.`} 
        />
        <meta name="keywords" content="AI tools categories, AI software directory, browse AI tools by category" />
        <link rel="canonical" href="https://tooliq.ai/categories" />
        
        <meta property="og:title" content="AI Tools Categories - Browse by Use Case" />
        <meta property="og:description" content={`Discover ${totalTools}+ AI tools organized into ${toolCategories.length} categories.`} />
        <meta property="og:url" content="https://tooliq.ai/categories" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "AI Tools Categories",
            "description": `Browse ${toolCategories.length} categories of AI tools`,
            "numberOfItems": toolCategories.length,
            "itemListElement": toolCategories.slice(0, 20).map((category, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "CreativeWork",
                "name": category.title,
                "description": `${category.tools.length} AI tools for ${category.title.toLowerCase()}`,
                "url": `https://tooliq.ai/categories/${slugify(category.title)}`
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
        <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                AI Tools <span className="text-transparent bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text">Categories</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                Discover {totalTools}+ AI tools organized into {toolCategories.length} specialized categories. 
                Find the perfect tool for your specific needs.
              </p>
              
              <div className="relative max-w-2xl mx-auto">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-6 py-4 pr-12 text-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm bg-white/80 backdrop-blur-sm"
                  placeholder="Search categories (e.g., 'writing', 'image', 'coding')..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{toolCategories.length}</div>
              <div className="text-gray-600">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">{totalTools}+</div>
              <div className="text-gray-600">AI Tools</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{totalFreeTools}+</div>
              <div className="text-gray-600">Free Tools</div>
            </div>
          </div>

          {filteredCategories.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCategories.map((category) => (
                <div
                  key={category.title}
                  onClick={() => navigate(`/categories/${slugify(category.title)}`)}
                  className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/60 p-6 cursor-pointer hover:shadow-xl hover:border-purple-300 transition-all duration-300 group hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center text-white text-lg font-bold mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    {category.title.charAt(0)}
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors line-clamp-2">
                    {category.title}
                  </h3>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      {category.tools.length} {category.tools.length === 1 ? 'tool' : 'tools'}
                    </span>
                    <span className="text-purple-600 group-hover:translate-x-1 transition-transform font-bold">
                      →
                    </span>
                  </div>

                  {category.tools.some(tool => tool.isFree) && (
                    <div className="mt-3 inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full border border-green-200">
                      <span>✨</span>
                      <span>Free tools</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">No categories found</h3>
              <p className="text-gray-600 max-w-md mx-auto mb-8">
                No categories matching "<span className="font-semibold text-purple-600">{searchTerm}</span>"
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl hover:from-purple-600 hover:to-blue-600 font-semibold"
              >
                Clear Search
              </button>
            </div>
          )}

          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white shadow-2xl">
              <h2 className="text-2xl font-bold mb-4">
                Ready to explore all tools?
              </h2>
              <p className="text-white/90 mb-6 max-w-2xl mx-auto">
                Browse {totalTools}+ AI tools with advanced filtering and search capabilities.
              </p>
              <button
                onClick={() => navigate('/tools')}
                className="px-8 py-4 bg-white text-purple-600 rounded-xl hover:bg-gray-100 font-bold transform hover:scale-105 transition-transform"
              >
                Browse All Tools
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryPage;